import React, { useState } from 'react';
import { motion } from "framer-motion";
import { Plus, Search, X, Edit2, Trash2, BookOpen, Video, Image as ImageIcon } from "lucide-react";
import { useData } from "@/contexts/DataContext";
import { Learning_Legacy } from "@/types/models";
import ImageUpload from "@/components/ImageUpload";
import RichTextEditor from "@/components/RichTextEditor";

const AdminLearning = () => {
  const { learning, addLearning, updateLearning, deleteLearning } = useData();
  const OTHER_CATEGORY_VALUE = "__other__";
  const presetLearningCategories = ["Finance", "Investing", "Stock Market", "Banking", "Business"];
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [editing, setEditing] = useState<Learning_Legacy | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [selectedCategoryOption, setSelectedCategoryOption] = useState<string>("Finance");
  const [customCategory, setCustomCategory] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const contentAlignmentOptions: Array<{ value: Learning_Legacy["contentAlignment"]; label: string }> = [
    { value: "justify", label: "Justified" },
    { value: "left", label: "Left" },
    { value: "center", label: "Center" },
    { value: "right", label: "Right" },
  ];
  const fontStyleOptions: Array<{ value: Learning_Legacy["fontStyle"]; label: string }> = [
    { value: "normal", label: "Normal" },
    { value: "times_new_roman", label: "Times New Roman" },
    { value: "courier_new", label: "Courier New" },
  ];

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filtered = !normalizedSearch
    ? learning
    : learning.filter((item) =>
        [
          item.title,
          item.category,
          item.instructor,
          item.description,
          item.status,
          item.difficultyLevel,
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalizedSearch)
      );
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedLearning = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const emptyLearning: Omit<Learning_Legacy, "id"> = {
    title: "",
    description: "",
    contentAlignment: "justify",
    fontStyle: "times_new_roman",
    category: "Finance",
    difficultyLevel: "beginner",
    duration: "",
    imageUrl: "",
    videoUrl: "",
    resourceUrl: "",
    instructor: "",
    status: "published",
  };

  const openNew = () => {
    setEditing({ id: Date.now().toString(), ...emptyLearning });
    setSelectedCategoryOption(emptyLearning.category);
    setCustomCategory("");
    setIsNew(true);
  };

  const openEdit = (item: Learning_Legacy) => {
    setEditing({ ...item });
    if (presetLearningCategories.includes(item.category)) {
      setSelectedCategoryOption(item.category);
      setCustomCategory("");
    } else {
      setSelectedCategoryOption(OTHER_CATEGORY_VALUE);
      setCustomCategory(item.category);
    }
    setIsNew(false);
  };

  const handleSave = async () => {
    if (!editing) return;
    const finalCategory =
      selectedCategoryOption === OTHER_CATEGORY_VALUE
        ? customCategory.trim()
        : selectedCategoryOption.trim();
    if (!finalCategory) {
      alert("Category is required.");
      return;
    }
    const payload = { ...editing, category: finalCategory };
    
    try {
      if (isNew) {
        await addLearning(payload);
      } else {
        await updateLearning(editing.id, payload);
      }
      setEditing(null);
      setCustomCategory("");
      setIsNew(false);
    } catch (error) {
      console.error("Error saving learning:", error);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this learning item?")) {
      try {
        await deleteLearning(id);
      } catch (error) {
        console.error("Error deleting learning:", error);
      }
    }
  };

  const update = (field: keyof Learning_Legacy, value: any) => {
    if (editing) {
      setEditing({ ...editing, [field]: value });
    }
  };
  const onSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Learning Management</h1>
        <button onClick={openNew} className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity">
          <Plus size={18} /> Add Learning
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <form onSubmit={onSearchSubmit} className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
            <input
              type="text"
              placeholder="Search learning content..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
          >
            <Search size={15} />
            Search
          </button>
        </form>
      </div>

      {/* Learning List */}
      <div className="finance-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-4 font-semibold">Title</th>
                <th className="text-left p-4 font-semibold">Category</th>
                <th className="text-left p-4 font-semibold">Difficulty</th>
                <th className="text-left p-4 font-semibold">Instructor</th>
                <th className="text-left p-4 font-semibold">Type</th>
                <th className="text-left p-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedLearning.map((item) => (
                <tr key={item.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {item.imageUrl && <ImageIcon size={16} className="text-blue-500" />}
                      {item.videoUrl && <Video size={16} className="text-red-500" />}
                      <span className="font-medium">{item.title}</span>
                    </div>
                  </td>
                  <td className="p-4 text-muted-foreground">{item.category}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      item.difficultyLevel === 'beginner' ? 'bg-green-100 text-green-800' :
                      item.difficultyLevel === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {item.difficultyLevel}
                    </span>
                  </td>
                  <td className="p-4 text-muted-foreground">{item.instructor}</td>
                  <td className="p-4">
                    <div className="flex gap-1">
                      {item.imageUrl && <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Image</span>}
                      {item.videoUrl && <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">Video</span>}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(item)} className="p-1.5 rounded hover:bg-muted">
                        <Edit2 size={15} />
                      </button>
                      <button onClick={() => handleDelete(item.id)} className="p-1.5 rounded hover:bg-destructive/10 text-destructive">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {filtered.length === 0 && (
        <div className="text-center py-6 text-sm text-muted-foreground">No learning content found.</div>
      )}
      {filtered.length > 0 && (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-muted-foreground">
            Showing {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
              disabled={safePage === 1}
              className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1.5 rounded-lg text-xs border ${
                  safePage === page ? "bg-primary text-primary-foreground border-primary" : "border-input"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))}
              disabled={safePage === totalPages}
              className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-card rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">{isNew ? "Add Learning" : "Edit Learning"}</h2>
                <button onClick={() => setEditing(null)} className="p-2 hover:bg-muted rounded-lg">
                  <X size={20} />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Title</label>
                  <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.title} onChange={(e) => update("title", e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Category</label>
                  <select
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm"
                    value={selectedCategoryOption}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      setSelectedCategoryOption(nextValue);
                      if (nextValue === OTHER_CATEGORY_VALUE) {
                        setCustomCategory("");
                        update("category", "");
                        return;
                      }
                      update("category", nextValue);
                    }}
                  >
                    {presetLearningCategories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                    <option value={OTHER_CATEGORY_VALUE}>Others</option>
                  </select>
                  {selectedCategoryOption === OTHER_CATEGORY_VALUE && (
                    <input
                      className="w-full mt-2 px-3 py-2 rounded-lg border border-input bg-background text-sm"
                      value={customCategory}
                      onChange={(e) => {
                        const nextCustomCategory = e.target.value;
                        setCustomCategory(nextCustomCategory);
                        update("category", nextCustomCategory);
                      }}
                      placeholder="Enter category name"
                    />
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Difficulty Level</label>
                  <select className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.difficultyLevel} onChange={(e) => update("difficultyLevel", e.target.value)}>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Duration</label>
                  <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.duration} onChange={(e) => update("duration", e.target.value)} placeholder="e.g., 2 hours, 5 days" />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Instructor</label>
                  <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.instructor} onChange={(e) => update("instructor", e.target.value)} />
                </div>
              </div>
              <div className="mt-4">
                <ImageUpload 
                  value={editing.imageUrl || ""} 
                  onChange={(url) => update("imageUrl", url)}
                  placeholder="Upload learning image or enter URL"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Video URL</label>
                  <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.videoUrl || ""} onChange={(e) => update("videoUrl", e.target.value)} placeholder="https://youtube.com/watch?v=..." />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Resource URL</label>
                  <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.resourceUrl} onChange={(e) => update("resourceUrl", e.target.value)} placeholder="https://example.com/resource" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-muted-foreground">Description</label>
                  <div className="mt-1">
                    <RichTextEditor
                      value={editing.description}
                      onChange={(value) => update("description", value)}
                      placeholder="Write rich description. Select text and apply Normal, Bold, H2, Link, or Undo."
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Content Alignment</label>
                  <select
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm"
                    value={editing.contentAlignment}
                    onChange={(e) => update("contentAlignment", e.target.value)}
                  >
                    {contentAlignmentOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Font Style</label>
                  <select
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm"
                    value={editing.fontStyle}
                    onChange={(e) => update("fontStyle", e.target.value)}
                  >
                    {fontStyleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => setEditing(null)} className="px-4 py-2 border border-border rounded-lg font-medium hover:bg-muted">
                  Cancel
                </button>
                <button onClick={handleSave} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity">
                  {isNew ? "Add Learning" : "Save Changes"}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminLearning;

